/**
 * Workspace API — Supabase (with Realtime subscriptions)
 */

import { supabase, TABLES, fromDbList } from './db';
import type { WorkspaceItem, WorkspaceItemStatus, WorkspaceMember, WorkspaceRole, Priority } from '../types';

// ── Members ───────────────────────────────────────────────────────────────────

export async function getWorkspaceMembers(taskId: string): Promise<WorkspaceMember[]> {
  try {
    const { data, error } = await supabase
      .from(TABLES.TASK_PARTICIPANTS)
      .select('user_id, display_name, role, added_at')
      .eq('task_id', taskId);

    if (error) throw error;
    return (data ?? []).map((d) => ({
      userId: d.user_id as string,
      displayName: (d.display_name as string) ?? 'Collaborator',
      role: ((d.role as WorkspaceRole) ?? 'collaborator'),
      joinedAt: (d.added_at as string) ?? new Date().toISOString(),
    }));
  } catch (e) {
    console.warn('getWorkspaceMembers:', e);
    return [];
  }
}

export async function updateMemberRole(
  taskId: string,
  memberId: string,
  role: WorkspaceRole,
  displayName?: string
): Promise<void> {
  const updates: Record<string, unknown> = { role };
  if (displayName) updates['display_name'] = displayName;
  const { error } = await supabase
    .from(TABLES.TASK_PARTICIPANTS)
    .update(updates)
    .eq('task_id', taskId)
    .eq('user_id', memberId);
  if (error) throw error;
}

// ── Workspace Items ────────────────────────────────────────────────────────────

export function subscribeWorkspaceItems(
  taskId: string,
  callback: (items: WorkspaceItem[]) => void,
  onError?: (err: Error) => void
): () => void {
  const load = async () => {
    const { data, error } = await supabase
      .from(TABLES.WORKSPACE_ITEMS)
      .select('*')
      .eq('task_id', taskId);

    if (error) { onError?.(error as Error); callback([]); return; }
    const items = fromDbList<WorkspaceItem>(data ?? []);
    items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    callback(items);
  };

  void load();

  const channel = supabase
    .channel(`workspace-items-${taskId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: TABLES.WORKSPACE_ITEMS, filter: `task_id=eq.${taskId}` },
      () => void load()
    )
    .subscribe((status) => {
      if (status === 'CHANNEL_ERROR') onError?.(new Error('Realtime channel error'));
    });

  return () => { void supabase.removeChannel(channel); };
}

export async function createWorkspaceItem(
  taskId: string,
  item: {
    title: string;
    description: string;
    assignedTo: string;
    assignedToName: string;
    assignedBy: string;
    assignedByName: string;
    priority: Priority;
  }
): Promise<string> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from(TABLES.WORKSPACE_ITEMS)
    .insert({
      task_id: taskId,
      title: item.title,
      description: item.description,
      assigned_to: item.assignedTo,
      assigned_to_name: item.assignedToName,
      assigned_by: item.assignedBy,
      assigned_by_name: item.assignedByName,
      priority: item.priority,
      status: 'pending',
      created_at: now,
      updated_at: now,
    })
    .select('id')
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function updateWorkspaceItemStatus(
  itemId: string,
  status: WorkspaceItemStatus,
  notes?: string
): Promise<void> {
  const updates: Record<string, unknown> = {
    status,
    updated_at: new Date().toISOString(),
    ...(status === 'completed' ? { completed_at: new Date().toISOString() } : {}),
    ...(notes !== undefined ? { notes } : {}),
  };
  const { error } = await supabase.from(TABLES.WORKSPACE_ITEMS).update(updates).eq('id', itemId);
  if (error) throw error;
}

export async function updateWorkspaceItem(
  itemId: string,
  updates: Partial<Pick<WorkspaceItem, 'title' | 'description' | 'priority' | 'assignedTo' | 'assignedToName' | 'notes'>>
): Promise<void> {
  const dbUpdates: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.title !== undefined) dbUpdates['title'] = updates.title;
  if (updates.description !== undefined) dbUpdates['description'] = updates.description;
  if (updates.priority !== undefined) dbUpdates['priority'] = updates.priority;
  if (updates.assignedTo !== undefined) dbUpdates['assigned_to'] = updates.assignedTo;
  if (updates.assignedToName !== undefined) dbUpdates['assigned_to_name'] = updates.assignedToName;
  if (updates.notes !== undefined) dbUpdates['notes'] = updates.notes;
  const { error } = await supabase.from(TABLES.WORKSPACE_ITEMS).update(dbUpdates).eq('id', itemId);
  if (error) throw error;
}

export async function deleteWorkspaceItem(itemId: string): Promise<void> {
  const { error } = await supabase.from(TABLES.WORKSPACE_ITEMS).delete().eq('id', itemId);
  if (error) throw error;
}

export async function getWorkspaceStats(taskId: string) {
  const { data, error } = await supabase
    .from(TABLES.WORKSPACE_ITEMS)
    .select('status')
    .eq('task_id', taskId);

  if (error) throw error;
  const items = (data ?? []) as Array<{ status: WorkspaceItemStatus }>;
  const total = items.length;
  const pending = items.filter((d) => d.status === 'pending').length;
  const inProgress = items.filter((d) => d.status === 'in-progress').length;
  const completed = items.filter((d) => d.status === 'completed').length;
  const blocked = items.filter((d) => d.status === 'blocked').length;
  return { total, pending, inProgress, completed, blocked };
}
